/**
 * Single blog article (magazine v2): thin scroll-progress bar + "copy link"
 * share button, plus (round 2 QA, 2026-08-27) a content-image gallery —
 * groups the article's inline images 2-per-row (1-per-row on mobile) and
 * wires them into a GLightbox lightbox with arrow navigation across every
 * image in the article. Facebook/LinkedIn share are plain outbound links
 * (no SDK, no tracking script) — nothing to wire up in JS for those.
 */
import GLightbox from 'glightbox';

export default function vijestiSingle() {
	initContentGallery();
	initProgressBar();
	initCopyLink();
}

/**
 * Content image gallery.
 *
 * Article bodies (checked several published posts, e.g. "Čarolija
 * praznika") author images as standalone `wp:image` blocks scattered
 * between headings/paragraphs — there is no existing markup grouping that
 * already puts them side by side. To satisfy "slike po dvije, kao
 * galerija" for every article (not just ones an editor happens to place
 * images consecutively in), this collects every top-level image block in
 * reading order and re-wraps them into `.vijesti-single__image-row` pairs
 * (CSS grid, see pages/_single.scss) — this DOES change an image's visual
 * position relative to its immediately-following paragraph (it now sits in
 * a shared row with the next image instead of alone), but preserves the
 * overall reading order (images still appear in the same relative sequence
 * they were authored in) and touches ONLY rendering, never the stored
 * post_content — safe to run on every article, no backend change needed.
 *
 * Only DIRECT CHILDREN of `.vijesti-single__content` that are a plain
 * Gutenberg "Image" block are treated as gallery images — checked several
 * published posts and found two markup variants for the same block:
 * `<figure class="wp-block-image ...">` directly, or (older editor
 * version) `<div class="wp-block-image"><figure>...</figure></div>`; both
 * are handled (`getGalleryWrapper()` below). Images nested inside other
 * blocks (tables, blockquotes, an existing multi-image Gallery block) are
 * deliberately left alone, since those already have their own internal
 * layout and re-grouping them here could conflict with it.
 *
 * Also deliberately EXCLUDED: images with WP's `alignleft`/`alignright`
 * block class (found on an older post, "Kako postaviti BEPRO gazišta") —
 * those are manually resized + float-wrapped beside a specific paragraph
 * by the original author (inline `style="width:...px"`), not full-width
 * gallery photos. Forcing one into the 2-column grid would fight its
 * manual sizing/float and could visibly break that specific old article —
 * exactly the kind of hand-authored old content the brief says not to
 * rework. `alignwide`/`alignfull`/`aligncenter`/unaligned images are still
 * block-level (no float), so those ARE grouped normally.
 */
function getGalleryWrapper(el) {
	let figure = null;

	if (el.tagName === 'FIGURE') {
		figure = el;
	} else if (el.tagName === 'DIV' && el.classList.contains('wp-block-image')) {
		figure = el.querySelector('figure');
	}

	if (!figure || !figure.querySelector('img')) {
		return null;
	}

	if (figure.classList.contains('alignleft') || figure.classList.contains('alignright')) {
		return null;
	}

	return el;
}

function initContentGallery() {
	const content = document.querySelector('.vijesti-single__content');

	if (!content) {
		return;
	}

	const figures = Array.from(content.children).filter((el) => getGalleryWrapper(el));

	if (!figures.length) {
		return;
	}

	for (let i = 0; i < figures.length; i += 2) {
		const row = document.createElement('div');
		row.className = 'vijesti-single__image-row';

		const first = figures[i];
		const second = figures[i + 1];

		first.parentNode.insertBefore(row, first);
		row.appendChild(first);

		if (second) {
			row.appendChild(second);
		} else {
			// Odd one out — full-width row instead of half-width with a gap
			// beside it (see &--single in pages/_single.scss).
			row.classList.add('vijesti-single__image-row--single');
		}
	}

	// Wrap every gallery image in (or reuse an existing) <a> for GLightbox,
	// and share one `data-gallery` group across the whole article so the
	// lightbox's arrows step through every image in the piece, regardless
	// of which row they're in.
	const images = Array.from(content.querySelectorAll('.vijesti-single__image-row img'));

	images.forEach((img) => {
		img.classList.add('vijesti-single__content-img');

		let link = img.closest('a');
		if (!link) {
			link = document.createElement('a');
			img.parentNode.insertBefore(link, img);
			link.appendChild(img);
		}

		// Full-size src the image already has (no separate "original" fetch
		// — the rendered content image, usually WP's "large" size, is
		// already large enough for a modal view and keeps this a
		// front-end-only change).
		link.setAttribute('href', img.currentSrc || img.getAttribute('src'));
		link.classList.add('glightbox');
		link.dataset.gallery = 'vijesti-article';
		// Deliberately no `data-glightbox` title/description string: that
		// attribute is GLightbox's own "key: value; key: value" mini
		// syntax, and arbitrary WP `alt` text can contain `:`/`;` which
		// would corrupt the parse. The brief only asks for arrow navigation
		// through the images, not captions, so this is skipped rather than
		// worked around.
	});

	if (!images.length) {
		return;
	}

	GLightbox({
		selector: '.glightbox',
		touchNavigation: true,
		loop: false,
		zoomable: false,
	});
}

function initProgressBar() {
	const bar = document.querySelector('.js-vijesti-progress');
	const content = document.querySelector('.vijesti-single__content');

	if (!bar || !content) {
		return;
	}

	const update = () => {
		const rect = content.getBoundingClientRect();
		const contentTop = rect.top + window.scrollY;
		const viewportHeight = window.innerHeight;

		const start = contentTop - viewportHeight * 0.3;
		const end = contentTop + content.offsetHeight - viewportHeight * 0.6;
		const total = Math.max(1, end - start);
		const progress = ((window.scrollY - start) / total) * 100;

		bar.style.width = `${Math.min(100, Math.max(0, progress))}%`;
	};

	update();
	window.addEventListener('scroll', update, { passive: true });
	window.addEventListener('resize', update);
}

function initCopyLink() {
	const button = document.querySelector('.js-vijesti-copy-link');

	if (!button) {
		return;
	}

	button.addEventListener('click', () => {
		const url = button.dataset.url || window.location.href;
		const label = button.querySelector('.js-vijesti-copy-label');
		const originalText = label ? label.textContent : '';

		const markCopied = () => {
			if (!label) {
				return;
			}
			label.textContent = 'Link kopiran';
			window.setTimeout(() => {
				label.textContent = originalText;
			}, 2000);
		};

		if (navigator.clipboard && window.isSecureContext) {
			navigator.clipboard.writeText(url).then(markCopied).catch(() => fallbackCopy(url, markCopied));
		} else {
			fallbackCopy(url, markCopied);
		}
	});
}

function fallbackCopy(url, done) {
	const input = document.createElement('textarea');
	input.value = url;
	input.setAttribute('readonly', '');
	input.style.position = 'absolute';
	input.style.left = '-9999px';
	document.body.appendChild(input);
	input.select();

	try {
		document.execCommand('copy');
	} catch (err) {
		// Clipboard unsupported — the link is still visible/shareable via the URL bar.
	}

	document.body.removeChild(input);
	done();
}
